<script setup>
import { reactive, watch, computed } from "vue";
import { useUserStore } from "@/stores/user";
import { useCrfStore } from "@/stores/crf";

// 定义组件属性
const props = defineProps({
  formModel: {},
  type: null
});

// 表单验证
const crfStore = useCrfStore();
// 表单对象（绑定选项）
const model = reactive(props.formModel);

// 排除标准字段列表（表单中的15项）
const excludeCriteriaFields = [
  "Budd-Chiari 综合征",
  "先天性肝纤维化",
  "结节病",
  "肝窦阻塞综合征（SOS）",
  "是否存在肝紫癜病？",
  "是否有骨髓移植史？",
  "是否有肝活检诊断为肝血吸虫病（孤立阳性血清学不是排除标准）？",
  "是否有心力衰竭或行Fontan手术？",
  "是否有Abernethy综合征？",
  "是否有遗传性出血性毛细血管扩张？",
  "是否有慢性胆汁淤积性疾病？（如PBC、PSC）",
  "是否有恶性肿瘤肝脏浸润？",
  "是否有淀粉样变？",
  "α1 抗胰蛋白酶缺乏症及肝豆状核变性等遗传代谢性肝脏疾病",
  "研究者认为不适合参加本研究的患者"
];

// 更新 isExclude 字段
const updateIsExclude = () => {
  // 极早期患者无需遵循排除标准
  if (model.fields['排除标准']?.['极早期患者'] === '是') {
    model.isExclude = 0;
    return;
  }

  // 检查排除标准15项，任一项为"是"则排除
  const shouldExclude = excludeCriteriaFields.some(field =>
    model.fields['排除标准']?.[field] === '是'
  );

  model.isExclude = shouldExclude ? 1 : 0;
};

// 如果排除表单极早期患者没有选择,就用入选表单的值
if (model.fields['排除标准'] && !model.fields['排除标准']['极早期患者']) {
  model.fields['排除标准']['极早期患者'] = model.fields['入选标准']['入选标准：极早期患者']
}

// 监听入选标准中的极早期患者字段变化，实时同步到排除标准
watch(
  () => model.fields?.['入选标准']?.['入选标准：极早期患者'],
  (newValue) => {
    if (model.fields['排除标准']) {
      model.fields['排除标准']['极早期患者'] = newValue;
      updateIsExclude();
    }
  }
)

// 监听排除标准字段变化，更新 isExclude
watch(
  () => model.fields?.['排除标准'],
  () => {
    updateIsExclude();
  },
  { deep: true }
)



// 节省代码长度，在模板中使用
const crfFields = computed(() => {
  return model.fields[`${props.type}`];
});

// 字段自动校验用于赋值 prop
const itemProp = function(prop) {
  return `fields['${props.type}']['${prop}']`;
};

</script>

<template>
  <template v-if="crfFields">
    <div class="box">
      <!-- 极早期患者-（noPH-PSVD） -->
        <el-form-item label="noPH-PSVD" :prop="itemProp('极早期患者')"
                    :rules="crfStore.radioRules('极早期患者')">
          <el-radio-group v-model="crfFields['极早期患者']">
            <el-radio label="是" prop="是"></el-radio>
            <el-radio label="否" prop="否"></el-radio>
          </el-radio-group>
        </el-form-item>
        <!-- <h2>排除标准</h2> -->
        <div class="title_p">
      
          <h2>排除标准</h2>
          <!-- <p>{{crfFields['极早期患者'] == '是' ? '（极早期患者无需遵循排除标准）' :''}}</p> -->
        </div>
        <div v-if="1 || !(model.fields[`${props.type}`]['极早期患者'] == '是')">

          <el-form-item label="1. Budd-Chiari 综合征"
                        :prop="itemProp('Budd-Chiari 综合征')"
                        :rules="crfStore.radioRules('Budd-Chiari 综合征')">
            <el-radio-group v-model="crfFields['Budd-Chiari 综合征']">
              <el-radio label="是" prop="是"></el-radio>
              <el-radio label="否" prop="否"></el-radio>
            </el-radio-group>
          </el-form-item>

          <el-form-item label="2. 先天性肝纤维化"
                        :prop="itemProp('先天性肝纤维化')"
                        :rules="crfStore.radioRules('先天性肝纤维化')">
            <el-radio-group v-model="crfFields['先天性肝纤维化']">
              <el-radio label="是" prop="是"></el-radio>
              <el-radio label="否" prop="否"></el-radio>
            </el-radio-group>
          </el-form-item>

          <el-form-item label="3. 结节病"
                        :prop="itemProp('结节病')"
                        :rules="crfStore.radioRules('结节病')">
            <el-radio-group v-model="crfFields['结节病']">
              <el-radio label="是" prop="是"></el-radio>
              <el-radio label="否" prop="否"></el-radio>
            </el-radio-group>
          </el-form-item>

          <el-form-item label="4. 肝窦阻塞综合征（SOS）"
                        :prop="itemProp('肝窦阻塞综合征（SOS）')"
                        :rules="crfStore.radioRules('肝窦阻塞综合征（SOS）')">
            <el-radio-group v-model="crfFields['肝窦阻塞综合征（SOS）']">
              <el-radio label="是" prop="是"></el-radio>
              <el-radio label="否" prop="否"></el-radio>
            </el-radio-group>
          </el-form-item>

          <el-form-item label="5. 是否存在肝紫癜病？"
                        :prop="itemProp('是否存在肝紫癜病？')"
                        :rules="crfStore.radioRules('是否存在肝紫癜病？')">
            <el-radio-group v-model="crfFields['是否存在肝紫癜病？']">
              <el-radio label="是" prop="是"></el-radio>
              <el-radio label="否" prop="否"></el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item label="6. 是否有骨髓移植史？"
                        :prop="itemProp('是否有骨髓移植史？')"
                        :rules="crfStore.radioRules('是否有骨髓移植史？')">
            <el-radio-group v-model="crfFields['是否有骨髓移植史？']">
              <el-radio label="是" prop="是"></el-radio>
              <el-radio label="否" prop="否"></el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item label="7. 是否有肝活检诊断为肝血吸虫病（孤立阳性血清学不是排除标准）？"
                        :prop="itemProp('是否有肝活检诊断为肝血吸虫病（孤立阳性血清学不是排除标准）？')"
                        :rules="crfStore.radioRules('是否有肝活检诊断为肝血吸虫病（孤立阳性血清学不是排除标准）？')">
            <el-radio-group v-model="crfFields['是否有肝活检诊断为肝血吸虫病（孤立阳性血清学不是排除标准）？']">
              <el-radio label="是" prop="是"></el-radio>
              <el-radio label="否" prop="否"></el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item label="8. 是否有心力衰竭或行Fontan手术？"
                        :prop="itemProp('是否有心力衰竭或行Fontan手术？')"
                        :rules="crfStore.radioRules('是否有心力衰竭或行Fontan手术？')">
            <el-radio-group v-model="crfFields['是否有心力衰竭或行Fontan手术？']">
              <el-radio label="是" prop="是"></el-radio>
              <el-radio label="否" prop="否"></el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item label="9. 是否有Abernethy综合征？"
                        :prop="itemProp('是否有Abernethy综合征？')"
                        :rules="crfStore.radioRules('是否有Abernethy综合征？')">
            <el-radio-group v-model="crfFields['是否有Abernethy综合征？']">
              <el-radio label="是" prop="是"></el-radio>
              <el-radio label="否" prop="否"></el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item label="10. 是否有遗传性出血性毛细血管扩张？"
                        :prop="itemProp('是否有遗传性出血性毛细血管扩张？')"
                        :rules="crfStore.radioRules('是否有遗传性出血性毛细血管扩张？')">
            <el-radio-group v-model="crfFields['是否有遗传性出血性毛细血管扩张？']">
              <el-radio label="是" prop="是"></el-radio>
              <el-radio label="否" prop="否"></el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item label="11. 是否有慢性胆汁淤积性疾病？（如PBC、PSC）"
                        :prop="itemProp('是否有慢性胆汁淤积性疾病？（如PBC、PSC）')"
                        :rules="crfStore.radioRules('是否有慢性胆汁淤积性疾病？（如PBC、PSC）')">
            <el-radio-group v-model="crfFields['是否有慢性胆汁淤积性疾病？（如PBC、PSC）']">
              <el-radio label="是" prop="是"></el-radio>
              <el-radio label="否" prop="否"></el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item label="12. 是否有恶性肿瘤肝脏浸润？"
                        :prop="itemProp('是否有恶性肿瘤肝脏浸润？')"
                        :rules="crfStore.radioRules('是否有恶性肿瘤肝脏浸润？')">
            <el-radio-group v-model="crfFields['是否有恶性肿瘤肝脏浸润？']">
              <el-radio label="是" prop="是"></el-radio>
              <el-radio label="否" prop="否"></el-radio>
            </el-radio-group>
          </el-form-item>
          <!-- <el-form-item label="17. 是否有先天性肝纤维化？"
                        :prop="itemProp('是否有先天性肝纤维化？')"
                        :rules="crfStore.radioRules('是否有先天性肝纤维化？')">
            <el-radio-group v-model="crfFields['是否有先天性肝纤维化？']">
              <el-radio label="是" prop="是"></el-radio>
              <el-radio label="否" prop="否"></el-radio>
            </el-radio-group>
          </el-form-item> -->
          <el-form-item label="13. 是否有淀粉样变？"
                        :prop="itemProp('是否有淀粉样变？')"
                        :rules="crfStore.radioRules('是否有淀粉样变？')">
            <el-radio-group v-model="crfFields['是否有淀粉样变？']">
              <el-radio label="是" prop="是"></el-radio>
              <el-radio label="否" prop="否"></el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item label="14. α1 抗胰蛋白酶缺乏症及肝豆状核变性等遗传代谢性肝脏疾病"
                        :prop="itemProp('α1 抗胰蛋白酶缺乏症及肝豆状核变性等遗传代谢性肝脏疾病')"
                        :rules="crfStore.radioRules('α1 抗胰蛋白酶缺乏症及肝豆状核变性等遗传代谢性肝脏疾病')">
            <el-radio-group v-model="crfFields['α1 抗胰蛋白酶缺乏症及肝豆状核变性等遗传代谢性肝脏疾病']">
              <el-radio label="是" prop="是"></el-radio>
              <el-radio label="否" prop="否"></el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item label="15. 研究者认为不适合参加本研究的患者"
                        :prop="itemProp('研究者认为不适合参加本研究的患者')"
                        :rules="crfStore.radioRules('研究者认为不适合参加本研究的患者')">
            <el-radio-group v-model="crfFields['研究者认为不适合参加本研究的患者']">
              <el-radio label="是" prop="是"></el-radio>
              <el-radio label="否" prop="否"></el-radio>
            </el-radio-group>
          </el-form-item>
        </div>
    </div>

  </template>
</template>

<style scoped lang="scss">

  .box ::v-deep{
    .el-form-item__label{
      height: auto !important;
      // 保持label文字为默认颜色，不显示红色
      color: #606266 !important;
    }
  }
  .title_p{
    position: relative;
    p{
      margin: 0;
      position: absolute;
      top: 5px;
      left: 150px;
      color: red;
      font-size: 12px;
    }
  }
  
</style>